import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircleOutline, MailOutline } from '@mui/icons-material';
import { IConfig } from '../../Interfaces/IConfig';
import { customFetcher } from '../../hooks/useFetch';
import { PublicLayout } from './PublicLayout';
import { academicTitles, businessForms, genders } from './publicFormConstants';

interface IProps {
  configObject: IConfig;
}

interface IValidateResponse {
  Text: string | null;
  Email: string;
}

type State = 'loading' | 'invalid' | 'decision' | 'form' | 'accepted' | 'declined';

/**
 * Öffentliche Seite, über die eine extern eingeladene Person (noch ohne
 * Benutzerkonto) eine Einladung zu einem Vorgang annehmen oder ablehnen kann.
 * Beim Annehmen werden die Stammdaten erfasst und ein Benutzerkonto angelegt;
 * die Person erhält anschließend per E-Mail (SystemMail 4) einen
 * Auto-Login-Link ins Mandantenportal, wo sie ihr Passwort vergibt.
 *
 * Entspricht funktional InviteDialogAddContact aus dem abgelösten
 * frontend-public-dialog, jedoch im Stil des neuen Mandantenportals.
 */
export const InviteAcceptContact: React.FC<IProps> = ({ configObject }) => {
  const theme = useTheme();
  const params = useParams<{ token: string }>();

  const [state, setState] = useState<State>('loading');
  const [invite, setInvite] = useState<IValidateResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Stammdaten
  const [idBusinessForm, setIdBusinessForm] = useState(1);
  const [idGender, setIdGender] = useState(1);
  const [idAcademicTitle, setIdAcademicTitle] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');

  const hasValidated = useRef(false);

  const isPerson = idBusinessForm === 1;

  useEffect(() => {
    if (hasValidated.current) {
      return;
    }
    hasValidated.current = true;

    if (!params.token) {
      setState('invalid');
      return;
    }

    customFetcher({
      endpoint: `/contactinvite/contact/validate/${params.token}`,
    })
      .then(res => {
        setInvite(res as IValidateResponse);
        setState('decision');
      })
      .catch(() => setState('invalid'));
  }, [params.token]);

  const submitAnswer = (isAssumed: boolean) => {
    if (!params.token) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);

    const body: Record<string, unknown> = {
      TempToken: params.token,
      isAssumed,
    };

    if (isAssumed) {
      // Das Backend (contactToLegalClient) benötigt zwingend ein CompanyObject,
      // auch für natürliche Personen. Adressfelder bleiben leer — die Anschrift
      // wird hier bewusst nicht erfasst.
      body.ContactObject = {
        idContact: -1,
        idCompany: -1,
        idGender,
        idAcademicTitle,
        idBusinessForm,
        FirstName: isPerson ? firstName : '',
        LastName: isPerson ? lastName : companyName,
        Email: invite?.Email ?? '',
        PhoneNumber: phone === '' ? null : phone,
        CompanyObject: {
          idCompany: -1,
          Company: isPerson ? '' : companyName,
          idBusinessForm,
          idPostcode: null,
          Street: '',
          StreetNr: '',
          StreetAditional: '',
          PhoneNumber: phone,
          Website: '',
          RegisterCourt: null,
          RegisterNumber: null,
          TaxNumber: null,
          SalesTaxNumber: null,
        },
      };
    }

    customFetcher({
      endpoint: `/contactinvite/answer/${params.token}`,
      method: 'POST',
      body,
    })
      .then(() => setState(isAssumed ? 'accepted' : 'declined'))
      .catch(() => setSubmitError(true))
      .finally(() => setIsSubmitting(false));
  };

  const formIsValid = isPerson
    ? firstName.trim() !== '' && lastName.trim() !== ''
    : companyName.trim() !== '';

  // ----- Render-Hilfen -------------------------------------------------------

  if (state === 'loading') {
    return (
      <PublicLayout
        configObject={configObject}
        title='Einladung zum Vorgang'
        subtitle='Einladung wird geprüft …'
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      </PublicLayout>
    );
  }

  if (state === 'invalid') {
    return (
      <PublicLayout
        configObject={configObject}
        title='Einladung zum Vorgang'
      >
        <Alert severity='warning'>
          Die Einladung konnte nicht gefunden werden oder ist bereits
          abgelaufen.
          <br />
          In dringenden Fällen kontaktieren Sie uns bitte.
        </Alert>
      </PublicLayout>
    );
  }

  if (state === 'accepted') {
    return (
      <PublicLayout
        configObject={configObject}
        title='Einladung angenommen'
        subtitle='Vielen Dank!'
      >
        <Alert severity='success' icon={<CheckCircleOutline fontSize='small' />}>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Ihr Benutzerkonto wird eingerichtet.</strong>
          </Typography>
          <Typography variant='body2'>
            Sie erhalten in Kürze eine E-Mail mit einem Link, über den Sie sich
            automatisch anmelden und Ihr Passwort vergeben können. Anschließend
            steht Ihnen der Vorgang im Mandantenportal zur Verfügung.
          </Typography>
        </Alert>
      </PublicLayout>
    );
  }

  if (state === 'declined') {
    return (
      <PublicLayout configObject={configObject} title='Einladung abgelehnt'>
        <Alert severity='info'>
          Sie haben die Einladung abgelehnt. Sofern Sie nicht bereits ein
          Benutzerkonto besitzen, werden alle im Zusammenhang mit dieser
          Einladung erfassten Daten gelöscht.
        </Alert>
      </PublicLayout>
    );
  }

  // ----- Entscheidung: Annehmen / Ablehnen -----------------------------------

  if (state === 'decision') {
    return (
      <PublicLayout
        configObject={configObject}
        title='Einladung zum Vorgang'
        subtitle={`Sie wurden zum Mandantenportal von ${configObject.company_name} eingeladen.`}
      >
        <Stack spacing={2.5}>
          {invite?.Email && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                E-Mail-Adresse
              </Typography>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 0.5 }}>
                <MailOutline fontSize='small' color='primary' />
                <Typography variant='body1' sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {invite.Email}
                </Typography>
              </Stack>
            </Box>
          )}

          {invite?.Text && invite.Text !== '' && (
            <Box>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.75 }}
              >
                Nachricht
              </Typography>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                {invite.Text}
              </Typography>
            </Box>
          )}

          <Alert severity='info'>
            Wenn Sie die Einladung annehmen, wird der entsprechende Vorgang Ihrem
            neuen Benutzerkonto im Mandantenportal zugeordnet.
          </Alert>

          {submitError && (
            <Alert severity='error'>
              Ihre Antwort konnte nicht übermittelt werden. Bitte versuchen Sie
              es erneut.
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1.5}
            justifyContent='space-between'
          >
            <Button
              color='error'
              variant='outlined'
              disabled={isSubmitting}
              onClick={() => submitAnswer(false)}
            >
              Ablehnen
            </Button>
            <Button
              color='primary'
              variant='contained'
              disabled={isSubmitting}
              onClick={() => setState('form')}
            >
              Annehmen
            </Button>
          </Stack>
        </Stack>
      </PublicLayout>
    );
  }

  // ----- Stammdaten-Formular -------------------------------------------------

  return (
    <PublicLayout
      configObject={configObject}
      title='Konto einrichten'
      subtitle='Bitte vervollständigen Sie Ihre Stammdaten. Ihr Passwort vergeben Sie anschließend bequem nach dem automatischen Login.'
    >
      <Stack spacing={2.25}>
        <TextField
          select
          label='Rechtsform'
          fullWidth
          value={idBusinessForm}
          onChange={e => setIdBusinessForm(Number(e.target.value))}
        >
          {businessForms.map(bf => (
            <MenuItem key={bf.idBusinessForm} value={bf.idBusinessForm}>
              {bf.BusinessForm}
            </MenuItem>
          ))}
        </TextField>

        {isPerson ? (
          <>
            <TextField
              select
              label='Anrede'
              fullWidth
              value={idGender}
              onChange={e => setIdGender(Number(e.target.value))}
            >
              {genders.map(g => (
                <MenuItem key={g.idGender} value={g.idGender}>
                  {g.Gender}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label='Titel'
              fullWidth
              value={idAcademicTitle}
              onChange={e => setIdAcademicTitle(Number(e.target.value))}
            >
              {academicTitles.map(t => (
                <MenuItem key={t.idAcademicTitle} value={t.idAcademicTitle}>
                  {t.AcademicTitle}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Vorname'
              fullWidth
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              error={firstName.trim() === ''}
              required
            />
            <TextField
              label='Nachname'
              fullWidth
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              error={lastName.trim() === ''}
              required
            />
          </>
        ) : (
          <TextField
            label='Gesellschaftsname'
            fullWidth
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
            error={companyName.trim() === ''}
            required
          />
        )}

        <TextField
          label='Telefonnummer'
          fullWidth
          value={phone}
          onChange={e =>
            setPhone(e.target.value.replace(/[^0-9+()/\s-]/g, ''))
          }
          inputProps={{ inputMode: 'tel' }}
        />

        {submitError && (
          <Alert severity='error'>
            Ihre Antwort konnte nicht übermittelt werden. Bitte versuchen Sie es
            erneut.
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          justifyContent='space-between'
          sx={{ pt: 1 }}
        >
          <Button
            variant='text'
            disabled={isSubmitting}
            onClick={() => setState('decision')}
          >
            Zurück
          </Button>
          <Button
            variant='contained'
            color='primary'
            disabled={isSubmitting || !formIsValid}
            onClick={() => submitAnswer(true)}
          >
            {isSubmitting ? 'Wird gesendet …' : 'Einladung annehmen'}
          </Button>
        </Stack>
      </Stack>
    </PublicLayout>
  );
};
